/**
 * 草稿里的 `styleTokens` 逐字段验型(V0)。
 *
 * ═══ 为什么必须验、且必须**重建**而不是原样返回 ═══════════════════════════════════
 * ① 草稿是 localStorage / 服务端 `formJson` 里的**陈年 JSON**:上个版本写的、被别的
 *    标签页写坏的、用户手改过的,都可能进来。长图那条路此前压根没验(`loadDraft` 验了
 *    blocks / useCase / width,唯独 `styleTokens` 直接进 state)。
 * ② ai 侧 `StyleTokens` 是 `extra="forbid"`(`style_tokens.py:15`)—— 多一个字段
 *    就是 **422**。所以这里不能 `{...raw}` 放行,得**只挑认识的字段重建一份**,
 *    把陈年多余字段挡在提交之外。
 * ③ 枚举同理:`material/lighting/tone/density/source` 任一是野值,ai 也 422。
 *
 * 形状不对就整份丢弃返回 `null`(调用方按"没有配色"处理)——
 * 半个配色比没有配色更糟:它会带着一半野值去出图。
 */
import type {
  Density,
  Lighting,
  Material,
  StyleTokens,
  StyleTone,
} from "../../longimage/model/canvas";

const HEX = /^#[0-9A-Fa-f]{6}$/;
const PALETTE_KEYS = ["primary", "secondary", "accent", "ink", "muted", "line", "bg"] as const;

const MATERIALS = new Set<Material>(["matte", "glossy", "frosted", "metallic", "natural"]);
const LIGHTINGS = new Set<Lighting>(["studio", "natural", "dramatic", "soft"]);
const TONES = new Set<StyleTone>([
  "premium_minimal",
  "vivid_promo",
  "warm_life",
  "tech_cool",
  "fresh_clean",
]);
const DENSITIES = new Set<Density>(["compact", "normal", "airy"]);
/** ★与 ai `TokenSource` 逐字对齐:多一个值就是 422(已核准事实①)。 */
const SOURCES = new Set<StyleTokens["source"]>(["auto", "preset", "manual"]);
/** ai `FontPair{title,body: str, max_length=40}`。 */
const FONT_MAX = 40;

export function parseStyleTokens(raw: unknown): StyleTokens | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Record<string, unknown>;

  const pal = d.palette as Record<string, unknown> | undefined;
  if (!pal || typeof pal !== "object") return null;
  const palette = {} as StyleTokens["palette"];
  for (const k of PALETTE_KEYS) {
    const v = pal[k];
    if (typeof v !== "string" || !HEX.test(v)) return null;
    palette[k] = v;
  }

  const { material, lighting, tone, density, source } = d as {
    material: Material;
    lighting: Lighting;
    tone: StyleTone;
    density: Density;
    source: StyleTokens["source"];
  };
  if (!MATERIALS.has(material) || !LIGHTINGS.has(lighting)) return null;
  if (!TONES.has(tone) || !DENSITIES.has(density) || !SOURCES.has(source)) return null;

  const fp = d.fontPair as Record<string, unknown> | undefined;
  if (!fp || typeof fp !== "object") return null;
  const { title, body } = fp;
  if (typeof title !== "string" || typeof body !== "string") return null;
  if (!title || !body || title.length > FONT_MAX || body.length > FONT_MAX) return null;

  // ★只挑认识的字段重建 —— 陈年草稿里的多余字段到此为止(ai extra="forbid")
  // brief = 整图风格自由段(2026-08-04 批①):可选,坏值按没有;80 = ai max_length 同步
  const brief = typeof d.brief === "string" ? d.brief.slice(0, 80) : "";
  // ★stylePreset = 风格库画风富文本(2026-08-20):**必须显式收** —— 这个函数只挑认识的字段重建,
  //   漏收就等于「选了画风,存一次草稿就没了」,而且一声不响。600 = ai max_length 同步。
  const stylePreset = typeof d.stylePreset === "string" ? d.stylePreset.slice(0, 600) : "";
  // ★userVisualDirective = 用户自己写的视觉/版式要求原话(2026-08-21):**必须显式收** ——
  //   同 stylePreset 那条血账,漏收就是「用户写的画面要求存一次草稿就没了」,而且一声不响。
  //   1200 = ai max_length 同步。
  const userVisualDirective =
    typeof d.userVisualDirective === "string" ? d.userVisualDirective.slice(0, 1200) : "";
  // ★paletteFromUser:布尔,坏值按 false(不硬猜"这色是用户写的")。
  const paletteFromUser = d.paletteFromUser === true;
  // ★sectionDirectives = 只属于某一段的视觉要求(LI-ROUTE-1):**必须显式收** ——
  //   本函数是**显式重建**的,不收 = 用户写给某一段的画面要求存一次草稿就没了,
  //   而且一声不响(与 userVisualDirective 同一条教训)。
  //   ★逐条校形:`outlineIndex` 必须是 ≥0 的整数(-1 属于全局那一格,不许混进来),
  //     `text` 非空。坏形状**整条丢弃**,不猜 —— 猜出来的段号会把要求送到别的块上。
  const sectionDirectives = Array.isArray(d.sectionDirectives)
    ? (d.sectionDirectives as unknown[])
        .filter(
          (x): x is { outlineIndex: number; text: string } =>
            !!x &&
            typeof x === "object" &&
            Number.isInteger((x as { outlineIndex?: unknown }).outlineIndex) &&
            ((x as { outlineIndex: number }).outlineIndex ?? -1) >= 0 &&
            typeof (x as { text?: unknown }).text === "string" &&
            ((x as { text: string }).text ?? "").length > 0,
        )
        .slice(0, 20)
        .map((x) => ({ outlineIndex: x.outlineIndex, text: x.text.slice(0, 1200) }))
    : [];
  return {
    palette,
    material,
    lighting,
    tone,
    density,
    fontPair: { title, body },
    source,
    ...(brief ? { brief } : {}),
    ...(stylePreset ? { stylePreset } : {}),
    ...(userVisualDirective ? { userVisualDirective } : {}),
    ...(paletteFromUser ? { paletteFromUser } : {}),
    ...(sectionDirectives.length ? { sectionDirectives } : {}),
  };
}
